import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function ContestoStoricoPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-indigo-50 to-blue-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 bg-indigo-100 px-4 py-2 rounded-full mb-6">
            <i className="ri-ancient-pavilion-line text-indigo-600"></i>
            <span className="text-indigo-800 text-sm font-semibold whitespace-nowrap">Storia e Cultura</span>
          </div>
          <h1 className="font-serif text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Contesto Storico-Culturale
          </h1>
          <p className="text-xl text-gray-700 leading-relaxed">
            L'Iliade nasce in un'epoca di grandi trasformazioni: scopriamo il mondo che ha dato vita al poema.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          {/* Historical Period */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">L'Epoca della Composizione</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
              <div>
                <img 
                  src="https://public.readdy.ai/ai/img_res/90d44f0481b9722fca7ffd6553583841.jpg" 
                  alt="Periodo Arcaico" 
                  className="w-full h-80 object-cover rounded-2xl mb-6"
                />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">VIII Secolo a.C.</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  L'Iliade viene composta durante il periodo arcaico greco (750-480 a.C.), un'epoca di rinascita dopo i "secoli bui" seguiti al crollo della civiltà micenea. È il tempo della nascita delle poleis (città-stato), della colonizzazione del Mediterraneo e della rinascita culturale.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  In questo periodo si diffonde l'alfabeto fenicio adattato al greco, permettendo la trascrizione dei poemi orali. Le città greche iniziano a organizzarsi politicamente e culturalmente, e l'identità ellenica comune si rafforza anche attraverso la condivisione dei poemi omerici.
                </p>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=mycenaean%20civilization%20bronze%20age%20greece%20palace%20ruins%20archaeological%20site%20ancient%20fortifications%20cyclopean%20walls%20historical%20period%20before%20trojan%20war&width=800&height=600&seq=mycenaean-001&orientation=landscape" 
                  alt="Civiltà Micenea" 
                  className="w-full h-80 object-cover rounded-2xl mb-6"
                />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">L'Età Micenea (1600-1100 a.C.)</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Gli eventi narrati nell'Iliade si collocano tradizionalmente intorno al 1200 a.C., durante l'età del bronzo tardo, quando la civiltà micenea era al suo apice. I Micenei erano guerrieri e navigatori che controllavano gran parte della Grecia e avevano contatti commerciali con tutto il Mediterraneo orientale.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Le scoperte archeologiche di Heinrich Schliemann a Micene e Troia (fine XIX secolo) hanno confermato l'esistenza storica di queste civiltà, anche se il confine tra storia e mito rimane sfumato.
                </p>
              </div>
            </div>

            <div className="bg-amber-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-time-line text-orange-600"></i>
                Timeline Storica
              </h3>
              <div className="space-y-6">
                <div className="flex gap-6">
                  <div className="flex-shrink-0 w-32 font-bold text-orange-600">1600-1100 a.C.</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Civiltà Micenea</h4>
                    <p className="text-gray-700">Età del bronzo, palazzi fortificati, scrittura Lineare B</p>
                  </div>
                </div>
                <div className="flex gap-6">
                  <div className="flex-shrink-0 w-32 font-bold text-orange-600">1200 a.C. circa</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Guerra di Troia (data tradizionale)</h4>
                    <p className="text-gray-700">Conflitto tra Achei e Troiani, distruzione di Troia VIIa</p>
                  </div>
                </div>
                <div className="flex gap-6">
                  <div className="flex-shrink-0 w-32 font-bold text-orange-600">1100-800 a.C.</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Medioevo Ellenico</h4>
                    <p className="text-gray-700">"Secoli bui", perdita della scrittura, tradizione orale</p>
                  </div>
                </div>
                <div className="flex gap-6">
                  <div className="flex-shrink-0 w-32 font-bold text-orange-600">750 a.C. circa</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Composizione dell'Iliade</h4>
                    <p className="text-gray-700">Omero cristallizza la tradizione orale in forma poetica</p>
                  </div>
                </div>
                <div className="flex gap-6">
                  <div className="flex-shrink-0 w-32 font-bold text-orange-600">550 a.C. circa</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Redazione Scritta</h4>
                    <p className="text-gray-700">Pisistrato ordina la trascrizione ufficiale ad Atene</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Society and Culture */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">La Società Eroica</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gray-100 rounded-xl p-6">
                <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-shield-cross-line text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Aristocrazia Guerriera</h3>
                <p className="text-gray-700 leading-relaxed">
                  La società descritta nell'Iliade è dominata da un'aristocrazia guerriera. I re-eroi (basileis) guidano i loro popoli in battaglia, e il valore militare è la principale fonte di prestigio sociale.
                </p>
              </div>
              <div className="bg-gray-100 rounded-xl p-6">
                <div className="w-14 h-14 bg-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-gift-line text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Economia del Dono</h3>
                <p className="text-gray-700 leading-relaxed">
                  Le relazioni sociali si basano sullo scambio di doni (xenia) e sul bottino di guerra. L'onore e il prestigio si misurano attraverso la ricchezza accumulata e distribuita.
                </p>
              </div>
              <div className="bg-gray-100 rounded-xl p-6">
                <div className="w-14 h-14 bg-amber-600 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-group-line text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Assemblee e Consigli</h3>
                <p className="text-gray-700 leading-relaxed">
                  Le decisioni importanti vengono prese in assemblee (agorà) dove i guerrieri discutono liberamente, anche se il potere finale resta ai re. È un'anticipazione della futura democrazia.
                </p>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Valori e Codice d'Onore</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                La società omerica è regolata da un rigido codice d'onore. I valori fondamentali includono:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex gap-3">
                  <i className="ri-star-line text-orange-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Aretè (eccellenza):</strong> la virtù eroica, l'essere i migliori in battaglia e nella vita</span>
                </li>
                <li className="flex gap-3">
                  <i className="ri-star-line text-orange-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Timè (onore):</strong> il riconoscimento sociale del proprio valore, da difendere a ogni costo</span>
                </li>
                <li className="flex gap-3">
                  <i className="ri-star-line text-orange-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Kleos (gloria):</strong> la fama immortale che sopravvive alla morte attraverso il canto dei poeti</span>
                </li>
                <li className="flex gap-3">
                  <i className="ri-star-line text-orange-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Xenia (ospitalità):</strong> il sacro dovere di accogliere e proteggere gli stranieri</span>
                </li>
                <li className="flex gap-3">
                  <i className="ri-star-line text-orange-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Aidòs (rispetto/vergogna):</strong> il senso di pudore e rispetto verso gli altri e verso se stessi</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Religion */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Religione e Mitologia</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=mount%20olympus%20home%20of%20greek%20gods%20majestic%20mountain%20peak%20clouds%20divine%20realm%20ancient%20greece%20mythology%20atmospheric%20lighting%20epic%20landscape&width=800&height=600&seq=olympus-gods-001&orientation=landscape" 
                  alt="Monte Olimpo" 
                  className="w-full h-80 object-cover rounded-2xl mb-6"
                />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Gli Dei dell'Olimpo</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Nell'Iliade, gli dei non sono entità astratte ma personaggi vividi e antropomorfi che intervengono costantemente negli affari umani. Vivono sul Monte Olimpo e si dividono tra sostenitori dei Greci (Atena, Era, Poseidone) e dei Troiani (Apollo, Afrodite, Ares).
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Zeus, padre degli dei, cerca di mantenere un equilibrio, ma anche lui è soggetto al Fato (Moira), una forza superiore che nemmeno gli dei possono contrastare. Questa tensione tra volontà divina e destino inevitabile è un tema centrale del poema.
                </p>
              </div>
              <div>
                <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-2xl p-8 mb-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Caratteristiche della Religione Omerica</h4>
                  <div className="space-y-4">
                    <div>
                      <h5 className="font-bold text-gray-900 mb-1">Politeismo</h5>
                      <p className="text-gray-700 text-sm">Molteplici divinità con funzioni e personalità distinte</p>
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 mb-1">Antropomorfismo</h5>
                      <p className="text-gray-700 text-sm">Gli dei hanno forma umana, emozioni e debolezze umane</p>
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 mb-1">Sacrifici e Rituali</h5>
                      <p className="text-gray-700 text-sm">Offerte agli dei per ottenere favori o placare l'ira divina</p>
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 mb-1">Oracoli e Presagi</h5>
                      <p className="text-gray-700 text-sm">Gli dei comunicano attraverso segni, sogni e profezie</p>
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 mb-1">Nessuna Morale Assoluta</h5>
                      <p className="text-gray-700 text-sm">Gli dei non sono modelli etici, ma forze potenti da rispettare</p>
                    </div>
                  </div>
                </div>
                <div className="bg-amber-50 rounded-xl p-6">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-information-line text-orange-600"></i>
                    Curiosità
                  </h4>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Nell'Iliade, gli dei mangiano ambrosia e bevono nettare, sostanze che conferiscono l'immortalità. Il loro sangue non è rosso ma "ichor", un fluido divino. Possono assumere qualsiasi forma e muoversi istantaneamente da un luogo all'altro.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Warfare */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">L'Arte della Guerra</h2>
            <div className="bg-gray-900 rounded-3xl p-12 text-white mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-bold mb-4 text-orange-400">Armamento e Tattiche</h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    I guerrieri omerici combattono principalmente come fanti pesanti, protetti da elmi di bronzo, corazze, scudi e armati di lance e spade. I carri da guerra, trainati da cavalli, servono principalmente per il trasporto sul campo di battaglia, non per il combattimento vero e proprio.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Le battaglie iniziano spesso con duelli tra campioni, seguiti da scontri di massa. L'arco è considerato un'arma meno nobile rispetto alla lancia, e gli arcieri hanno uno status inferiore.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4 text-orange-400">Il Codice del Guerriero</h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Prima di combattere, i guerrieri si presentano, vantano le proprie imprese e quelle dei loro antenati. Il combattimento è regolato da norme non scritte: rispetto per i caduti, restituzione dei corpi per la sepoltura, trattamento dei prigionieri.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Tuttavia, la guerra è anche brutale: i vinti vengono spogliati delle armi (spoglie), le città conquistate vengono saccheggiate, le donne ridotte in schiavitù.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-shield-line text-4xl text-gray-900"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Scudo</h4>
                <p className="text-gray-700 text-sm">Grande scudo rotondo in bronzo e cuoio, decorato con scene mitologiche</p>
              </div>
              <div className="border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-knife-line text-4xl text-gray-900"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Lancia</h4>
                <p className="text-gray-700 text-sm">Arma principale, usata per colpire da vicino o lanciata contro il nemico</p>
              </div>
              <div className="border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-t-shirt-line text-4xl text-gray-900"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Corazza</h4>
                <p className="text-gray-700 text-sm">Armatura in bronzo che protegge il torso, spesso decorata riccamente</p>
              </div>
            </div>
          </div>

          {/* Cultural Impact */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">L'Eredità Culturale</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              L'Iliade non è solo un testo letterario, ma il fondamento della cultura greca classica. Per secoli è stata il principale strumento educativo, insegnando ai giovani greci la lingua, i valori, la storia e l'identità comune. I versi omerici venivano imparati a memoria e citati in ogni occasione pubblica.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Influenza sulla Letteratura</h3>
                <p className="text-gray-700 leading-relaxed">
                  Tutti i generi letterari greci - tragedia, commedia, lirica, storiografia - si ispirano a Omero. Autori come Eschilo, Sofocle ed Euripide rielaborano i miti omerici. Anche la letteratura romana (Virgilio, Ovidio) e quella europea moderna devono molto all'Iliade.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Influenza sull'Arte</h3>
                <p className="text-gray-700 leading-relaxed">
                  Le scene dell'Iliade decorano vasi, sculture, mosaici e affreschi per tutta l'antichità. Nel Rinascimento e oltre, artisti come Rubens, David e Canova rappresentano gli eroi omerici. Ancora oggi, cinema e fumetti si ispirano all'epica greca.
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-16 flex flex-col sm:flex-row gap-4 justify-between">
            <Link to="/introduzione" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              <span className="whitespace-nowrap">Introduzione</span>
            </Link>
            <Link to="/personaggi" className="flex items-center gap-2 text-orange-600 hover:text-orange-700 transition-colors font-semibold cursor-pointer">
              <span className="whitespace-nowrap">Personaggi Principali</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
